import { NavLink } from "react-router";
import {
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
} from "@/components/ui/sidebar";
import AccessGuard from "@/components/access-guard";
import { FeatureFlag } from "@/constants";
import { useIsTicketingEnabled } from "@/hooks/useIsTicketingEnabled";
import { useFlags } from "launchdarkly-react-client-sdk";
import { IlluminateUserPermissionsEnum } from "@/api/openapi";
import { useUser } from "@/contexts/user";
import { hasPermission } from "@/utils/user-permission";

interface MaintenanceSidebarGroupProps {
    basePath: string;
}

export const MaintenanceSidebarGroup = ({
    basePath,
}: MaintenanceSidebarGroupProps) => {
    const { canViewTickets } = useIsTicketingEnabled();
    const flags = useFlags();
    const { user } = useUser();
    const hasMaintenanceFeatureFlag =
        flags[FeatureFlag.IL_3705_PREVENTATIVE_PLANNED_MAINTENANCE];
    const hasPpmRead = hasPermission(
        user,
        IlluminateUserPermissionsEnum.PpmRead,
    );
    const shouldShowMaintenance =
        canViewTickets || (hasMaintenanceFeatureFlag && hasPpmRead);

    if (!shouldShowMaintenance) {
        return null;
    }

    return (
        <SidebarGroup>
            <SidebarGroupLabel>Maintenance</SidebarGroupLabel>
            <SidebarGroupContent>
                <SidebarMenu>
                    <AccessGuard
                        unauthorizedBehaviour={"hide"}
                        overrideAndRestrict={!canViewTickets}
                    >
                        <SidebarMenuItem>
                            <SidebarMenuButton asChild>
                                <NavLink
                                    className={"text-default-color"}
                                    to={`${basePath}/maintenance/tickets`}
                                >
                                    <span>Tickets</span>
                                </NavLink>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </AccessGuard>
                    <AccessGuard
                        featureFlags={[
                            FeatureFlag.IL_3705_PREVENTATIVE_PLANNED_MAINTENANCE,
                        ]}
                        permissions={[IlluminateUserPermissionsEnum.PpmRead]}
                        permissionsSelector="all"
                        unauthorizedBehaviour={"hide"}
                    >
                        <SidebarMenuItem>
                            <SidebarMenuButton asChild>
                                <NavLink
                                    className={"text-default-color"}
                                    to={`${basePath}/maintenance/service-events`}
                                >
                                    <span>Service events</span>
                                </NavLink>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </AccessGuard>
                    <AccessGuard
                        featureFlags={[
                            FeatureFlag.IL_3705_PREVENTATIVE_PLANNED_MAINTENANCE,
                        ]}
                        permissions={[IlluminateUserPermissionsEnum.PpmRead]}
                        permissionsSelector="all"
                        unauthorizedBehaviour={"hide"}
                    >
                        <SidebarMenuItem>
                            <SidebarMenuButton asChild>
                                <NavLink
                                    className={"text-default-color"}
                                    to={`${basePath}/maintenance/schedules`}
                                >
                                    <span>Schedules</span>
                                </NavLink>
                            </SidebarMenuButton>
                        </SidebarMenuItem>
                    </AccessGuard>
                </SidebarMenu>
            </SidebarGroupContent>
        </SidebarGroup>
    );
};
